import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import {
  type APIResponse,
  type ReportsMatterRecordRequestsReport,
} from "@/lib/types";
import type { ReportDateRangeType } from "@/lib/utils/reports/report-filter-options";

type MatterRecordRequestsReportDateRange = {
  endDate?: string;
  preset: ReportDateRangeType;
  startDate?: string;
};

type MatterRecordRequestsReportFilters = {
  assigneeUserIds?: string[];
  incidentReportStatuses?: string[];
  matterStatusLabels?: string[];
  incidentTypeLabels?: string[];
  incidentTypeIds?: string[];
  claimTypeIds?: string[];
  medicalTreatmentBillRecordStatuses?: string[];
  medicalTreatmentRecordStatuses?: string[];
  teamLabels?: string[];
};

export type RetrieveReportsMatterRecordRequestsReportInput = {
  dateRange: MatterRecordRequestsReportDateRange;
  filters?: MatterRecordRequestsReportFilters;
};

type MatterRecordRequestsReportResponse = {
  rows?: MatterRecordRequestsReportRow[];
};

type MatterRecordRequestsApiResponse = Omit<
  APIResponse<MatterRecordRequestsReportResponse>,
  "pagination"
> & {
  pagination?: { hasMore?: boolean };
};

type MatterRecordRequestsReportRow = {
  assigneeUserIds?: string[];
  dateCreated?: string;
  dateModified?: string;
  id: string;
  itemType?: string;
  matterId?: string;
  matterStatusLabel?: string;
  matterTitle?: string;
  incidentTypeLabel?: string | null;
  claimTypes?: Array<{ id: string; label: string }>;
  receivedDate?: string | null;
  rejectedDate?: string | null;
  requestDate?: string | null;
  requestLabel?: string | null;
  requestStatus?: string | null;
  teams?: Array<{ label?: string | null }>;
};

const getRequestKind = (itemType?: string) => {
  switch (itemType) {
    case "INCIDENT_REPORT":
      return "Incident Report";
    case "MEDICAL_TREATMENT_BILL":
      return "Medical Bill";
    case "MEDICAL_TREATMENT_RECORD":
      return "Medical Record";
    default:
      return itemType;
  }
};

const toReportRow = (
  row: MatterRecordRequestsReportRow,
): ReportsMatterRecordRequestsReport => ({
  claimTypes: row.claimTypes ?? [],
  dateCreated: row.dateCreated,
  dateModified: row.dateModified,
  matterRecordRequestsReportId: row.id,
  matterRecordRequestsReportMatterAssigneeUserIds: row.assigneeUserIds,
  matterRecordRequestsReportMatterId: row.matterId,
  matterRecordRequestsReportMatterStatusLabel: row.matterStatusLabel,
  matterRecordRequestsReportMatterTeams: (row.teams ?? []).flatMap((team) =>
    team.label ? [team.label] : [],
  ),
  matterRecordRequestsReportMatterTitle: row.matterTitle,
  matterRecordRequestsReportIncidentType: row.incidentTypeLabel,
  matterRecordRequestsReportReceivedDate: row.receivedDate ?? undefined,
  matterRecordRequestsReportRejectedDate: row.rejectedDate ?? undefined,
  matterRecordRequestsReportRequestDate: row.requestDate ?? undefined,
  matterRecordRequestsReportRequestKind: getRequestKind(row.itemType),
  matterRecordRequestsReportRequestLabel: row.requestLabel ?? undefined,
  matterRecordRequestsReportRequestStatus: row.requestStatus ?? undefined,
});

const PAGE_SIZE = 250;
const MAX_PAGES = 1000;

export const retrieveReportsMatterRecordRequestsReportV1 = async (
  data: RetrieveReportsMatterRecordRequestsReportInput,
) => {
  const rows: ReportsMatterRecordRequestsReport[] = [];
  let pageNumber = 1;
  let meta:
    | APIResponse<ReportsMatterRecordRequestsReport[]>["meta"]
    | undefined;
  let hasMore = false;

  do {
    const response =
      await authenticatedFastApiAxiosInstance.post<MatterRecordRequestsApiResponse>(
        "reports/v2/matter-record-requests/query",
        {
          dateRange: data.dateRange,
          filters: data.filters ?? {},
          pageNumber,
          pageSize: PAGE_SIZE,
        },
      );

    meta = response.data.meta;
    rows.push(...(response.data.data.rows ?? []).map(toReportRow));
    hasMore = response.data.pagination?.hasMore === true;
    pageNumber += 1;
  } while (hasMore && pageNumber <= MAX_PAGES);

  if (hasMore) {
    throw new Error(
      `The record requests report exceeded the ${MAX_PAGES}-page retrieval limit.`,
    );
  }

  return {
    data: rows,
    meta,
  } satisfies APIResponse<ReportsMatterRecordRequestsReport[]>;
};
